import React, { JSX, useState, useEffect } from 'react';
import Checkbox from '@mui/material/Checkbox';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material';
import useRolePermissions from '../../../../shared/hooks/useRolePermissions';
import { ROLES_CONTENT_PROPS } from '../../../../shared/constants/constant-data';

interface RegionAssetsProps {
  globalRegions: any[];
  globalDevicesFilter: any[];
  setGlobalRegions: (data: any[]) => void;
  setGlobalDevicesFilter: (data: any[]) => void;
  extractIdLabelTree: (regions: any[]) => any[];
  setGlobalRegionsIdLabel: (data: any[]) => void;
  fetchGlobalDevices: (minimalRegions: any[]) => Promise<void>;
  loadingRegions: boolean;
  loadingDevices: boolean;
  onAllRegionsSelectedChange: (allSelected: boolean) => void;
}

const RegionAssets: React.FC<RegionAssetsProps> = ({
  globalRegions,
  globalDevicesFilter,
  setGlobalRegions,
  setGlobalDevicesFilter,
  extractIdLabelTree,
  setGlobalRegionsIdLabel,
  fetchGlobalDevices,
  loadingRegions,
  loadingDevices,
  onAllRegionsSelectedChange,
}) => {
  const [expandedRegions, setExpandedRegions] = useState<{ [key: string]: boolean }>({});
  const [expandedDevices, setExpandedDevices] = useState<{ [key: string]: boolean }>({});
  const [regionSearch, setRegionSearch] = useState('');
  //const [deviceSearch, setDeviceSearch] = useState('');
  const { hasPermissions } = useRolePermissions();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  // Notify parent when all regions are selected or not
 useEffect(() => {
  if (globalRegions?.length > 0 || globalDevicesFilter?.length > 0) {
    const atLeastOneRegionSelected = globalRegions?.some(item => item.selected);
    const atLeastOneDeviceSelected = globalDevicesFilter?.some(item => item.selected);

    const flag = atLeastOneRegionSelected && atLeastOneDeviceSelected;

    onAllRegionsSelectedChange(flag);
  }
}, [globalRegions, globalDevicesFilter, onAllRegionsSelectedChange]);


  const renderSkeletonTree = (count = 5) => (
    <div>
      {Array.from({ length: count }).map((_, idx) => (
        <Skeleton
          variant="rectangular"

          key={idx}
          animation="wave"
          sx={{ borderRadius: 1 }}
          height={30} style={{ marginBottom: 10 }}
        />

      ))}
    </div>
  );

  const updateParentSelectionState = (node: any): any => {
    if (!node.data || node.data.length === 0) return node;

    node.data = node.data.map(updateParentSelectionState);

    const allSelected = node.data.every((child:any) => child.selected && !child.indeterminate);
    const noneSelected = node.data.every((child:any) => !child.selected && !child.indeterminate);

    if (allSelected) {
      node.selected = true;
      node.indeterminate = false;
    } else if (noneSelected) {
      node.selected = false;
      node.indeterminate = false;
    } else {
      node.selected = false;
      node.indeterminate = true;
    }

    return node;
  };

  const toggleExpand = (id: string, type: 'region' | 'device') => {
    if (type === 'region') {
      setExpandedRegions(prev => ({ ...prev, [id]: !prev[id] }));
    } else {
      setExpandedDevices(prev => ({ ...prev, [id]: !prev[id] }));
    }
  };




  const handleItemCheck = (item: any, isChecked: boolean, type: 'region' | 'device') => {
    const updateSelectionWithIndeterminate = (
      items: any[],
      targetId: number,
      isChecked: boolean
    ): any[] => {
      const applyToAllChildren = (children: any[], checked: boolean): any[] =>
        children.map(child => ({
          ...child,
          selected: checked,
          indeterminate: false,
          data: child.data ? applyToAllChildren(child.data, checked) : undefined,
        }));

      const update = (nodes: any[]): any[] =>
        nodes.map(node => {
          if (node.id === targetId) {
            const updatedChildren = node.data
              ? applyToAllChildren(node.data, isChecked)
              : undefined;

            return {
              ...node,
              selected: isChecked,
              indeterminate: false,
              data: updatedChildren,
            };
          }

          if (node.data) {
            return {
              ...node,
              data: update(node.data),
            };
          }

          return node;
        });

      return update(items).map(updateParentSelectionState);
    };

    if (type === 'region') {
      const updated = updateSelectionWithIndeterminate(globalRegions, item.id, isChecked);
      setGlobalRegions(updated);
      const minimalRegions = extractIdLabelTree(updated);
      setGlobalRegionsIdLabel(minimalRegions);

      // 🆕 Fetch devices for selected regions
      fetchGlobalDevices(minimalRegions);
    } else {
      const updated = updateSelectionWithIndeterminate(globalDevicesFilter, item.id, isChecked);
      setGlobalDevicesFilter(updated);
    }
  };

  const handleCheckAll = (isChecked: boolean, type: 'region' | 'device') => {
    const updateAll = (items: any[]): any[] =>
      items.map(item => ({
        ...item,
        selected: isChecked,
        indeterminate: false,
        data: item.data ? updateAll(item.data) : undefined,
      }));

    const updated = updateAll(type === 'region' ? globalRegions : globalDevicesFilter)
      .map(updateParentSelectionState);

    if (type === 'region') {
      setGlobalRegions(updated);
    } else {
      setGlobalDevicesFilter(updated);
    }
  };

  const handleExpandAll = (type: 'region' | 'device', expand: boolean) => {
    const expandAll = (items: any[], expandedState: any = {}) => {
      if (!Array.isArray(items)) return expandedState;
      items.forEach(item => {
        expandedState[item.id] = expand;
        if (Array.isArray(item.data) && item.data.length > 0) {
          expandAll(item.data, expandedState);
        }
      });
      return expandedState;
    };

    if (type === 'region') {
      setExpandedRegions(expandAll(globalRegions));
    } else {
      setExpandedDevices(expandAll(globalDevicesFilter));
    }
  };

  const filterTree = (items: any[], searchText: string): any[] => {
    if (!searchText) return items;

    const lowerSearch = searchText.toLowerCase();

    return items
      .map(item => {
        if (item.label.toLowerCase().includes(lowerSearch)) {
          return item;
        }
        if (item.data) {
          const filteredChildren = filterTree(item.data, searchText);
          if (filteredChildren.length > 0) {
            return { ...item, data: filteredChildren };
          }
        }
        return null;
      })
      .filter(Boolean);
  };


  const renderTree = (items: any[], type: 'region' | 'device') => {
    if (!Array.isArray(items)) return null;
    return items.map(item => (
      <div key={item.id} style={{ marginLeft: 20, marginTop: 5 }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {Array.isArray(item.data) && item.data.length > 0 && (
            <div onClick={() => toggleExpand(item.id, type)} style={{ cursor: 'pointer', marginRight: 5 }}>
              {type === 'region'
                ? expandedRegions[item.id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />
                : expandedDevices[item.id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </div>
          )}
          <Checkbox
            size="small"
            checked={item.selected}
            indeterminate={item.indeterminate}
            onChange={(e) => handleItemCheck(item, e.target.checked, type)}
            disabled={!hasPermissions(ROLES_CONTENT_PROPS.MODIFY_ROLE_REGION_DEVICE)}
          />
          <span>{item.label}</span>
        </div>
        {Array.isArray(item.data) && ((type === 'region' && expandedRegions[item.id]) || (type === 'device' && expandedDevices[item.id])) && (
          <div style={{ marginLeft: 20 }}>
            {renderTree(item.data, type)}
          </div>
        )}
      </div>
    ));
  };

  return (
    <>
      <div style={{ gap: '50px' }}>
        <div >
          {(Array.isArray(globalRegions) && Array.isArray(globalDevicesFilter) && globalRegions.length > 0 || globalDevicesFilter.length > 0) && (<input
            type="text"
            placeholder="Search Regions and Assets..."
            value={regionSearch}
            onChange={(e) => setRegionSearch(e.target.value)}
            style={{
              width: '100%',
              marginBottom: '10px',
              padding: '8px 12px',
              borderRadius: '4px',
              border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.23)' : '1px solid rgba(0, 0, 0, 0.23)',
              backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
              color: isDarkMode ? '#ffffff' : '#000000',
              outline: 'none',
            }}
          />)}



        </div>
        <div style={{ display: "flex", flexDirection: "row", gap: 200 }} >
          <div style={{ display: "flex", flexDirection: "column", width: "50%" }}>
            {Array.isArray(globalRegions) && globalRegions.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, gap: 10 }}>
                <Checkbox
                  size="small"
                  checked={globalRegions.every(item => item.selected && !item.indeterminate)}
                  indeterminate={
                    globalRegions.some(item => item.selected || item.indeterminate) &&
                    !globalRegions.every(item => item.selected && !item.indeterminate)
                  }
                  onChange={(e) => handleCheckAll(e.target.checked, 'region')}
                   disabled={!hasPermissions(ROLES_CONTENT_PROPS.MODIFY_ROLE_REGION_DEVICE)}
                />
                <b>All</b>
                {/* <button onClick={() => handleExpandAll('region', true)}>Expand All</button>
                    <button onClick={() => handleExpandAll('region', false)}>Collapse All</button> */}
              </div>
            )}
            <div>
              {loadingRegions
                ? renderSkeletonTree(6)
                : renderTree(filterTree(globalRegions, regionSearch), 'region')}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", width: "50%" }}>
            {globalDevicesFilter.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10, gap: 10 }}>
                <Checkbox
                  size="small"
                  checked={globalDevicesFilter.every(item => item.selected && !item.indeterminate)}
                  indeterminate={
                    globalDevicesFilter.some(item => item.selected || item.indeterminate) &&
                    !globalDevicesFilter.every(item => item.selected && !item.indeterminate)
                  }
                  onChange={(e) => handleCheckAll(e.target.checked, 'device')}
                   disabled={!hasPermissions(ROLES_CONTENT_PROPS.MODIFY_ROLE_REGION_DEVICE)}
                />
                <b>All</b>
                {/* <button onClick={() => handleExpandAll('device', true)}>Expand All</button>
                    <button onClick={() => handleExpandAll('device', false)}>Collapse All</button> */}
              </div>

            )}
            <div>
              {loadingDevices
                ? renderSkeletonTree(12)
                : renderTree(filterTree(globalDevicesFilter, regionSearch), 'device')}
            </div>
          </div>
        </div>
      </div>
    </>

  );
};

export default RegionAssets;
